import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { analyticsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';
import StatCard from '../../components/dashboard/StatCard';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const CHART_COLORS = [
    'rgb(99, 102, 241)',
    'rgb(34, 197, 94)',
    'rgb(251, 146, 60)',
    'rgb(168, 85, 247)',
    'rgb(236, 72, 153)',
    'rgb(14, 165, 233)',
    'rgb(234, 179, 8)',
    'rgb(20, 184, 166)',
    'rgb(244, 63, 94)',
    'rgb(59, 130, 246)',
];

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i++;
    }
    return `${value.toFixed(1)} ${units[i]}`;
};

const formatNumber = (value) => {
    const n = Number(value) || 0;
    return n.toLocaleString();
};

const sectionHeader = (icon, title, sub) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
            <h5 className="mb-0">
                <i className={`bi ${icon} me-2 text-primary`}></i>
                {title}
            </h5>
            {sub && <small className="text-muted">{sub}</small>}
        </div>
    </div>
);

const Analytics = () => {
    const [analytics, setAnalytics] = useState(null);
    const [period, setPeriod] = useState('30days');
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    useEffect(() => {
        fetchAnalytics();
    }, [period]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const res = await analyticsAPI.getOverview({ period });
            if (res?.data?.success) {
                setAnalytics(res.data.data);
            }
        } catch (error) {
            console.error('Analytics error:', error);
            setToast({ show: true, message: 'Failed to fetch analytics', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        const from = analytics?.period?.from || new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
        const to = analytics?.period?.to || new Date().toISOString().slice(0, 10);
        try {
            const res = await analyticsAPI.exportAnalytics(from, to);
            const blob = res?.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `nisa-care-analytics-${from}-to-${to}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setToast({ show: true, message: 'Analytics exported successfully', type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            setToast({ show: true, message: 'Failed to export analytics', type: 'error' });
        }
    };

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    title: (items) => items[0]?.label || '',
                },
            },
        },
        scales: {
            y: { beginAtZero: true, ticks: { precision: 0 } },
        },
    }), []);

    const doughnutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
    }), []);

    if (loading) {
        return <LoadingSpinner message="Loading analytics..." />;
    }

    if (!analytics) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-bar-chart fs-1 text-muted d-block mb-3"></i>
                <p className="text-muted">No analytics data available</p>
                <button className="btn btn-outline-primary" onClick={fetchAnalytics}>
                    <i className="bi bi-arrow-clockwise me-2"></i>Retry
                </button>
            </div>
        );
    }

    const users = analytics.users || {};
    const engagement = analytics.engagement || {};
    const content = analytics.content || {};
    const system = analytics.system || {};
    const periodFrom = analytics.period?.from || '';
    const periodTo = analytics.period?.to || '';

    // ---------- Chart data ----------
    const growthData = {
        labels: users.growth_trend?.labels || [],
        datasets: [
            {
                label: 'New Users',
                data: users.growth_trend?.data || [],
                borderColor: CHART_COLORS[0],
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    const activeUsersData = {
        labels: engagement.active_users_trend?.labels || [],
        datasets: [
            {
                label: 'Active Users',
                data: engagement.active_users_trend?.data || [],
                borderColor: CHART_COLORS[3],
                backgroundColor: 'rgba(168, 85, 247, 0.12)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    const engagementTrendData = {
        labels: engagement.daily_logs_trend?.labels || [],
        datasets: [
            { label: 'Daily Logs', data: engagement.daily_logs_trend?.data || [], borderColor: CHART_COLORS[0], backgroundColor: 'rgba(99,102,241,0.15)', fill: true, tension: 0.3 },
            { label: 'Prayers', data: engagement.prayer_trend?.data || [], borderColor: CHART_COLORS[1], backgroundColor: 'rgba(34,197,94,0.12)', fill: true, tension: 0.3 },
            { label: 'Tasbih', data: engagement.tasbih_trend?.data || [], borderColor: CHART_COLORS[2], backgroundColor: 'rgba(251,146,60,0.12)', fill: true, tension: 0.3 },
            { label: 'Qada', data: engagement.qada_trend?.data || [], borderColor: CHART_COLORS[4], backgroundColor: 'rgba(236,72,153,0.12)', fill: true, tension: 0.3 },
        ],
    };

    const habitFastingData = {
        labels: engagement.habit_trend?.labels || [],
        datasets: [
            { label: 'Habit Completions', data: engagement.habit_trend?.data || [], borderColor: CHART_COLORS[0], backgroundColor: 'rgba(99,102,241,0.6)' },
            { label: 'Fasts Completed', data: engagement.fasting_trend?.data || [], borderColor: CHART_COLORS[1], backgroundColor: 'rgba(34,197,94,0.6)' },
        ],
    };

    const deviceData = {
        labels: (system.devices || []).map((d) => d.device_type || 'Unknown'),
        datasets: [
            {
                data: (system.devices || []).map((d) => d.count),
                backgroundColor: (system.devices || []).map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                borderWidth: 2,
            },
        ],
    };

    const authProviderData = {
        labels: (users.by_auth_provider || []).map((p) => p.auth_provider || 'Unknown'),
        datasets: [
            {
                data: (users.by_auth_provider || []).map((p) => p.count),
                backgroundColor: (users.by_auth_provider || []).map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                borderWidth: 2,
            },
        ],
    };

    const madhabData = {
        labels: (users.by_madhab || []).map((m) => m.madhab || 'Unknown'),
        datasets: [
            {
                data: (users.by_madhab || []).map((m) => m.count),
                backgroundColor: (users.by_madhab || []).map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                borderWidth: 2,
            },
        ],
    };

    const countryData = {
        labels: (users.by_country || []).map((c) => c.country || 'Unknown'),
        datasets: [
            {
                label: 'Users',
                data: (users.by_country || []).map((c) => c.count),
                backgroundColor: (users.by_country || []).map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
            },
        ],
    };

    const prayerByNameData = {
        labels: (engagement.prayer_by_name || []).map((p) => p.prayer_name || 'Unknown'),
        datasets: [
            {
                label: 'Completions',
                data: (engagement.prayer_by_name || []).map((p) => p.count),
                backgroundColor: (engagement.prayer_by_name || []).map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                borderWidth: 2,
            },
        ],
    };

    const engagementStats = engagement.overview || {};
    const userStats = users.overview || {};
    const contentStats = content.overview || {};
    const broadcastStats = system.broadcasts || {};
    const notificationStats = system.notifications || {};
    const mediaStats = system.media || {};
    const sessionStats = system.sessions || {};

    return (
        <div>
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            <div className="page-header mb-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h2 className="page-title">System Analytics</h2>
                        <p className="page-subtitle">
                            {periodFrom && periodTo
                                ? `Overview from ${periodFrom} to ${periodTo}`
                                : 'Insights and metrics overview'}
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <select
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                        >
                            <option value="7days">Last 7 Days</option>
                            <option value="30days">Last 30 Days</option>
                            <option value="90days">Last 90 Days</option>
                        </select>
                        <button className="btn btn-outline-primary" onClick={fetchAnalytics} title="Refresh">
                            <i className="bi bi-arrow-clockwise"></i>
                        </button>
                        <button className="btn btn-outline-success" onClick={handleExport}>
                            <i className="bi bi-download me-2"></i>Export CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* ==================== KPI CARDS ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <StatCard
                        icon="bi-people-fill"
                        label="Total Users"
                        value={formatNumber(userStats.total_users)}
                        trend={formatNumber(userStats.new_today)}
                        trendLabel="new today"
                        color="primary"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-person-check-fill"
                        label="Active Users (30d)"
                        value={formatNumber(userStats.active_users)}
                        trend={userStats.active_users ? Math.round((userStats.active_users / (userStats.total_users || 1)) * 100) + '%' : '0%'}
                        trendLabel="of total"
                        color="success"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-gem"
                        label="Premium Users"
                        value={formatNumber(users.premium_stats?.premium_count)}
                        trend={`${users.premium_stats?.premium_percentage || 0}%`}
                        trendLabel="of total"
                        color="warning"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-clock-history"
                        label="Deleted Accounts"
                        value={formatNumber(userStats.deleted)}
                        trend={formatNumber(userStats.new_this_week)}
                        trendLabel="new this week"
                        color="danger"
                    />
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <StatCard
                        icon="bi-file-text-fill"
                        label="Articles"
                        value={formatNumber(contentStats.published_articles)}
                        trend={`${contentStats.total_articles || 0} total`}
                        color="primary"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-book-fill"
                        label="Duas"
                        value={formatNumber(contentStats.published_duas)}
                        trend={`${contentStats.total_duas || 0} total`}
                        color="info"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-lightbulb-fill"
                        label="Daily Insights"
                        value={formatNumber(content.insights?.active_insights)}
                        trend={`${content.insights?.total_insights || 0} total`}
                        color="warning"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-eye-fill"
                        label="Total Views"
                        value={formatNumber(contentStats.total_views)}
                        trend={formatNumber(engagementStats.prayer_completions)}
                        trendLabel="prayers completed"
                        color="success"
                    />
                </div>
            </div>

            {/* ==================== GROWTH & ACTIVITY ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <div className="stat-card">
                        {sectionHeader('bi-graph-up-arrow', 'New User Growth', 'Registrations per day in period')}
                        <div style={{ height: '280px' }}>
                            <Line data={growthData} options={chartOptions} />
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="stat-card">
                        {sectionHeader('bi-people-fill', 'Active Users', 'Distinct users with activity per day')}
                        <div style={{ height: '280px' }}>
                            <Line data={activeUsersData} options={chartOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== ENGAGEMENT TRENDS ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <div className="stat-card">
                        {sectionHeader('bi-activity', 'Engagement Trends', 'Daily logs, prayers, tasbih & qada completions')}
                        <div style={{ height: '300px' }}>
                            <Line data={engagementTrendData} options={chartOptions} />
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="stat-card">
                        {sectionHeader('bi-heart-pulse-fill', 'Habits & Fasting', 'Completions per day')}
                        <div style={{ height: '300px' }}>
                            <Bar data={habitFastingData} options={chartOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== FEATURE USAGE ==================== */}
            <div className="stat-card mb-4">
                {sectionHeader('bi-grid-fill', 'Feature Adoption', 'Distinct users who used each feature')}
                <div className="row g-3">
                    {(Object.entries(engagement.feature_usage || {}).length > 0 ? Object.entries(engagement.feature_usage) : [
                        ['daily_logs', 0], ['habits', 0], ['notes', 0], ['prayers', 0],
                    ]).map(([key, value]) => (
                        <div className="col-md-3 col-6" key={key}>
                            <div className="p-3 rounded-3 text-center" style={{ background: 'rgba(99,102,241,0.06)' }}>
                                <h4 className="mb-1 text-primary">{formatNumber(value)}</h4>
                                <small className="text-muted text-capitalize">{key.replace(/_/g, ' ')}</small>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ==================== DEMOGRAPHICS ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-lg-4">
                    <div className="stat-card">
                        {sectionHeader('bi-phone-fill', 'Auth Providers', 'How users sign up')}
                        <div style={{ height: '260px' }}>
                            <Doughnut data={authProviderData} options={doughnutOptions} />
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="stat-card">
                        {sectionHeader('bi-diagram-3-fill', 'By Madhab', 'Distribution of madhab')}
                        <div style={{ height: '260px' }}>
                            <Doughnut data={madhabData} options={doughnutOptions} />
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="stat-card">
                        {sectionHeader('bi-device-hdd-fill', 'Devices', 'Device type breakdown')}
                        <div style={{ height: '260px' }}>
                            <Doughnut data={deviceData} options={doughnutOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== COUNTRIES & PRAYERS ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-lg-7">
                    <div className="stat-card">
                        {sectionHeader('bi-geo-alt-fill', 'Top Countries', 'User distribution by country')}
                        <div style={{ height: '280px' }}>
                            <Bar data={countryData} options={{ ...chartOptions, indexAxis: 'y' }} />
                        </div>
                    </div>
                </div>
                <div className="col-lg-5">
                    <div className="stat-card">
                        {sectionHeader('bi-patch-check-fill', 'Prayers Completed', 'Completions by prayer in period')}
                        <div style={{ height: '280px' }}>
                            <Doughnut data={prayerByNameData} options={doughnutOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== TOP CONTENT ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-lg-6">
                    <div className="stat-card">
                        {sectionHeader('bi-file-text-fill', 'Top Articles', 'By total views')}
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Title</th>
                                        <th className="text-end">Views</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(content.top_articles || []).length > 0 ? content.top_articles.map((article, i) => (
                                        <tr key={article.id}>
                                            <td className="text-muted">{i + 1}</td>
                                            <td>
                                                <span className="text-truncate d-inline-block" style={{ maxWidth: '280px' }}>{article.title}</span>
                                                {article.category && <small className="text-muted d-block">{article.category}</small>}
                                            </td>
                                            <td className="text-end fw-semibold">{formatNumber(article.views_count)}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="3" className="text-center text-muted py-4">No article data yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="stat-card">
                        {sectionHeader('bi-book-fill', 'Top Duas', 'By bookmark count')}
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Title</th>
                                        <th className="text-end">Bookmarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(content.top_duas || []).length > 0 ? content.top_duas.map((dua, i) => (
                                        <tr key={dua.id}>
                                            <td className="text-muted">{i + 1}</td>
                                            <td>
                                                <span className="text-truncate d-inline-block" style={{ maxWidth: '280px' }}>{dua.title}</span>
                                                {dua.category_id && <small className="text-muted d-block">Category #{dua.category_id}</small>}
                                            </td>
                                            <td className="text-end fw-semibold">{formatNumber(dua.bookmark_count)}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="3" className="text-center text-muted py-4">No dua data yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ==================== SYSTEM & NOTIFICATIONS ==================== */}
            <div className="row g-4 mb-4">
                <div className="col-lg-4">
                    <div className="stat-card h-100">
                        {sectionHeader('bi-bell-fill', 'Notifications', 'Broadcast & notification stats')}
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Total Broadcasts</span>
                            <strong>{formatNumber(broadcastStats.total_broadcasts)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Sent</span>
                            <strong>{formatNumber(broadcastStats.sent_broadcasts)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Scheduled</span>
                            <strong>{formatNumber(broadcastStats.scheduled_broadcasts)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Total Recipients</span>
                            <strong>{formatNumber(broadcastStats.total_recipients)}</strong>
                        </div>
                        <div className="d-flex justify-content-between py-2">
                            <span className="text-muted">All Notifications</span>
                            <strong>{formatNumber(notificationStats.total_notifications)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-top py-2">
                            <span className="text-muted">Unread</span>
                            <strong className="text-warning">{formatNumber(notificationStats.unread_notifications)}</strong>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="stat-card h-100">
                        {sectionHeader('bi-shield-lock-fill', 'Sessions & Devices', 'Live session activity')}
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Active User Sessions</span>
                            <strong>{formatNumber(sessionStats.active_user_sessions)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Active Admin Sessions</span>
                            <strong>{formatNumber(sessionStats.active_admin_sessions)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">User Devices Connected</span>
                            <strong>{formatNumber(sessionStats.active_user_devices)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-bottom py-2">
                            <span className="text-muted">Admin Devices Connected</span>
                            <strong>{formatNumber(sessionStats.active_admin_devices)}</strong>
                        </div>
                        <div className="d-flex justify-content-between border-top py-2 mt-3">
                            <span className="text-muted">Media Files</span>
                            <strong>{formatNumber(mediaStats.total_media)} ({mediaStats.images || 0} img / {mediaStats.videos || 0} vid)</strong>
                        </div>
                        <div className="d-flex justify-content-between py-2">
                            <span className="text-muted">Media Storage Used</span>
                            <strong>{formatBytes(mediaStats.total_size_bytes)}</strong>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="stat-card h-100">
                        {sectionHeader('bi-person-video3', 'Admin Activity', 'Recent actions by admins')}
                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table className="table table-sm table-hover mb-0">
                                <tbody>
                                    {(analytics.recent_activity || []).map((activity, i) => (
                                        <tr key={i}>
                                            <td className="align-middle">
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar-circle me-2">
                                                        {activity.admin_name?.charAt(0).toUpperCase() || 'A'}
                                                    </div>
                                                    <div>
                                                        <strong className="d-block">{activity.admin_name}</strong>
                                                        <small className="text-muted text-capitalize">
                                                            {String(activity.action || '').replace(/_/g, ' ')}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-end align-middle">
                                                <small className="text-muted">
                                                    {new Date(activity.created_at).toLocaleDateString()}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                    {(analytics.recent_activity || []).length === 0 && (
                                        <tr><td className="text-center text-muted py-3">No activity yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-2 text-end">
                            <Link to="/activity-log" className="btn btn-sm btn-outline-secondary">
                                View Full Log
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
